// SeoLongDescription.jsx
// Generic reusable component for clamped HTML long descriptions (SEO footer pattern).
// Used in category pages and can be reused in Footer.
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sanitize } from 'isomorphic-dompurify';
import parse from 'html-react-parser';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { color } from '../style/variables.js';
import media from '../style/media.js';

const HEADING_REGEX = /^(<h[1-6][^>]*>.*?<\/h[1-6]>)/is;

/**
 * Parses an HTML string to separate the first heading element (h1-h6) from the remaining body content.
 * This is used for SEO descriptions where the heading needs special styling.
 *
 * IMPORTANT STRUCTURE REQUIREMENT:
 * The translation values for these keys MUST start with a heading tag (<h1> to <h6>),
 * e.g., "<h1>Heading Title</h1><p>Body paragraph...</p>".
 * If the heading tag is missing or not placed at the very beginning of the string,
 * `parseHeadingAndBody` will fail to extract the heading, causing styling or layout to break.
 *
 * @param {string} html - The sanitized HTML content.
 * @returns {{heading: string, body: string}}
 */
const parseHeadingAndBody = html => {
  const match = html.match(HEADING_REGEX);
  if (match) {
    return { heading: match[1], body: html.slice(match[0].length) };
  }
  return { heading: '', body: html };
};

const SeoLongDescription = ({
  content = null,
  className = undefined,
  clampLines = 2,
  noSpaceWithContent = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const prevContentRef = useRef(content);

  useEffect(() => {
    const contentChanged = prevContentRef.current !== content;
    prevContentRef.current = content;

    const expanded = contentChanged ? false : isExpanded;
    if (contentChanged && isExpanded) {
      setIsExpanded(false);
    }

    if (textRef.current && !expanded) {
      setIsTruncated(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
  }, [content, isExpanded]);

  if (!content) return null;

  const sanitized = sanitize(content);
  const parsedContent = parseHeadingAndBody(sanitized);

  return (
    <Wrapper className={className}>
      {parsedContent.heading && (
        <Heading>{parse(parsedContent.heading)}</Heading>
      )}
      <TextContainer>
        <ClampText
          ref={textRef}
          $isExpanded={isExpanded}
          $clampLines={clampLines}
          $noSpaceWithContent={noSpaceWithContent}
        >
          {parse(parsedContent.body)}
        </ClampText>
        {!isExpanded && isTruncated && (
          <SeeMoreButton onClick={() => setIsExpanded(true)}>
            ...{t('more', { ns: TranslationNamespace.GENERAL })}
          </SeeMoreButton>
        )}
      </TextContainer>
    </Wrapper>
  );
};

SeoLongDescription.propTypes = {
  content: PropTypes.string,
  className: PropTypes.string,
  clampLines: PropTypes.number,
  noSpaceWithContent: PropTypes.bool,
};

const Wrapper = styled.div``;

const Heading = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: ${color.grey[700]};
    margin: 0;
    letter-spacing: 0.016px;
  }
`;

const TextContainer = styled.div`
  position: relative;
`;

const ClampText = styled.div`
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
  letter-spacing: 0.016px;
  color: ${color.grey[700]};
  overflow: hidden;
  margin: ${({ $noSpaceWithContent }) =>
    $noSpaceWithContent ? '0px' : '8px 0 0 0'};
  max-height: ${({ $isExpanded, $clampLines }) =>
    $isExpanded ? 'none' : `${$clampLines * 1.6}em`};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    font-size: 16px;
    line-height: 1.5;
    font-weight: 400;
    margin: 0 0 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${color.tealBlue};
    text-decoration: underline;
  }
  ${media.mobile`
     margin: ${({ $noSpaceWithContent }) =>
       $noSpaceWithContent ? '0px' : '4px 0 0 0'};
  `};
`;

const SeeMoreButton = styled.button`
  ${ResetButtonStyle}
  position: absolute;
  right: 0;
  bottom: 2px;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.016px;
  color: ${color.grey[500]};
  cursor: pointer;
  padding-left: 4px;
  background: ${color.black};
`;

export { SeoLongDescription };
export default SeoLongDescription;
